import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, Lock } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";

export default function Checkout() {
  const { items, subtotal, shipping, total } = useCart();
  const nav = useNavigate();
  const [form, setForm] = useState({ fullName: "", email: "", phone: "", address: "", city: "", state: "", pincode: "" });

  if (!items.length) {
    return (
      <div className="bg-[var(--bg)] pt-[110px] md:pt-[120px] pb-20 min-h-screen">
        <div className="max-w-[760px] mx-auto px-6 md:px-10 text-center">
          <h1 className="font-serif-display text-3xl mt-10 font-semibold">Your cart is empty.</h1>
          <Link to="/products" data-testid="checkout-shop" className="btn-primary mt-6 inline-flex">Shop Now</Link>
        </div>
      </div>
    );
  }

  const submit = (e) => {
    e.preventDefault();
    const req = ["fullName", "email", "phone", "address", "city", "state", "pincode"];
    if (req.some((k) => !form[k])) { toast.error("Please fill all shipping details."); return; }
    sessionStorage.setItem("nt_shipping", JSON.stringify(form));
    nav("/checkout/payment");
  };

  return (
    <div data-testid="page-checkout" className="bg-[var(--bg)] pt-[110px] md:pt-[120px] pb-20 min-h-screen">
      <div className="max-w-[1280px] mx-auto px-4 md:px-10">
        <Steps current={1} />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-8">
          <form onSubmit={submit} data-testid="checkout-form" className="lg:col-span-7 card-soft p-6 md:p-8 space-y-4">
            <h2 className="font-serif-display text-2xl font-semibold text-[var(--ink)]">Shipping Address</h2>
            <Field testId="checkout-name" label="Full name *" value={form.fullName} onChange={(v) => setForm({ ...form, fullName: v })} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field testId="checkout-email" type="email" label="Email *" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <Field testId="checkout-phone" label="Phone *" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            </div>
            <Field testId="checkout-address" label="Address *" value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field testId="checkout-city" label="City *" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
              <Field testId="checkout-state" label="State *" value={form.state} onChange={(v) => setForm({ ...form, state: v })} />
              <Field testId="checkout-pincode" label="Pincode *" value={form.pincode} onChange={(v) => setForm({ ...form, pincode: v })} />
            </div>
            <button type="submit" data-testid="checkout-submit" className="btn-primary mt-3">Continue to Payment <ArrowRight size={14} /></button>
            <p className="kbd text-[0.6rem] text-[var(--ink-3)] flex items-center gap-2 mt-2"><Lock size={11} /> Your info is encrypted in transit.</p>
          </form>

          <aside className="lg:col-span-5">
            <div className="card-soft p-6 sticky top-[100px]">
              <h3 className="font-serif-display text-xl font-semibold mb-5">Order ({items.length})</h3>
              <div className="space-y-3 max-h-[280px] overflow-auto">
                {items.map((i) => (
                  <div key={i.id} className="flex gap-3 items-center">
                    <div className="w-14 h-14 rounded-md bg-white border border-[var(--line-soft)] overflow-hidden shrink-0"><img src={i.image} alt={i.name} className="w-full h-full object-cover" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[var(--ink)] truncate">{i.name}</div>
                      <div className="kbd text-[0.55rem] text-[var(--ink-3)] mt-0.5">Qty {i.qty} · {i.weight}</div>
                    </div>
                    <div className="text-sm font-semibold text-[var(--green)]">₹{(typeof i.price === "number" ? i.price : parseInt(String(i.price).replace(/[^\d]/g, ""), 10) || 0) * i.qty}</div>
                  </div>
                ))}
              </div>
              <div className="border-t border-[var(--line-soft)] mt-5 pt-4 space-y-2 text-sm">
                <Row label="Subtotal" value={`₹${subtotal}`} />
                <Row label="Shipping" value={shipping === 0 ? "Free" : `₹${shipping}`} />
                <div className="flex justify-between pt-3 border-t border-[var(--line-soft)]"><span className="font-semibold">Total</span><span className="font-serif-display text-2xl font-semibold text-[var(--green)]">₹{total}</span></div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

export function Steps({ current }) {
  const all = ["Cart", "Shipping", "Payment", "Done"];
  return (
    <div className="flex items-center gap-2 overflow-x-auto">
      {all.map((s, i) => (
        <div key={s} className={`flex items-center gap-2 shrink-0 ${i + 1 < current ? "text-[var(--green)]" : i + 1 === current ? "text-[var(--ink)]" : "text-[var(--ink-3)]"}`}>
          <span className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center ${i + 1 === current ? "bg-[var(--green)] text-white" : i + 1 < current ? "bg-[var(--gold)] text-[var(--green-deep)]" : "bg-[var(--bg-cream)] border border-[var(--line)]"}`}>{i + 1}</span>
          <span className="kbd text-[0.65rem]">{s}</span>
          {i < all.length - 1 && <span className="text-[var(--line)] mx-1">→</span>}
        </div>
      ))}
    </div>
  );
}

function Field({ testId, label, value, onChange, type = "text" }) {
  return (
    <div className="flex flex-col">
      <label className="kbd text-[var(--ink-3)] mb-1.5 text-[0.6rem]">{label}</label>
      <input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="bg-white border border-[var(--line)] rounded-md px-4 py-2.5 text-sm focus:border-[var(--green)] focus:outline-none" />
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between"><span className="text-[var(--ink-2)]">{label}</span><span className="font-semibold">{value}</span></div>;
}
